import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, Megaphone, Share2, FileText, ShoppingBag, Linkedin, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { EmailOptInForm } from "../components/EmailOptInForm";
import { GUIDES } from "../data/guides";
import { RESOURCES_PAGE } from "../constants/testIds";

const PROMPT_CATEGORIES = [
  { icon: Mail, label: "Cold & Nurture Emails", prompts: [
    "R.O.A.S. cold outreach opener (Role, Objective, Audience, Style)",
    "Follow-up email referencing a specific case study result",
    "Objection-handling email for the #1 reason prospects say no",
    "Low-pressure urgency email with a real deadline",
    "Breakup email that makes it easy to say no",
  ] },
  { icon: Megaphone, label: "Ads & Sales Pages", prompts: [
    "AIDA-framework headline for a landing page hero",
    "PAS-framework primary text for a Meta ad",
    "Google ad headline + description pair (3 variations)",
    "Sales page objection-handling FAQ section",
  ] },
  { icon: Share2, label: "Social Media", prompts: [
    "Instagram caption hook under 8 words + soft CTA",
    "TikTok script outline: hook / problem / demo / CTA",
    "Facebook caption adapted from a blog post summary",
    "LinkedIn thought-leadership post opener",
  ] },
  { icon: FileText, label: "Blog & SEO", prompts: [
    "SEO content brief: target keyword + recommended headings",
    "Meta description under 155 characters",
    "Listicle intro that states the reader's problem in 2 sentences",
    "Featured-snippet-optimized answer paragraph",
  ] },
  { icon: ShoppingBag, label: "Ecommerce", prompts: [
    "Feature-to-benefit product description (short + long version)",
    "Abandoned cart email sequence opener",
    "Product page bullet points from a raw spec sheet",
    "Gift-buyer-targeted product description variant",
  ] },
  { icon: Linkedin, label: "Personal Brand", prompts: [
    "LinkedIn bio: role + niche + credibility + CTA formula",
    "LinkedIn 'About' section long-form version",
    "Personal brand statement for a portfolio site",
    "Freelancer availability post announcing open slots",
  ] },
];

const CASE_STUDIES = GUIDES.filter((g) => g.slug === "phrasly-case-study-email-copy" || g.slug === "comparing-ai-writing-quality");
const TEMPLATE_GUIDES = GUIDES.filter((g) => g.slug === "email-sequence-framework" || g.slug === "social-media-content-calendar" || g.slug === "product-description-generator-ecommerce");

export default function Resources() {
  let idx = 0;
  return (
    <>
      <SEO title="Free AI Writing Prompts, Templates & Case Studies" description="Get our 20+ AI writing prompts library, tested email sequence templates, and real case studies — all free." />

      <section className="pt-36 pb-20 px-6 lg:px-10 spotlight">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-mono-tag text-xs uppercase tracking-[0.2em] text-gold mb-4">Resources</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="font-display font-black text-4xl sm:text-5xl text-white tracking-tight">The AI Writing Prompts Library</motion.h1>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto">20+ proven prompts across emails, ads, social, blog, ecommerce, and personal brand — built from every test on this site.</p>
          <div className="mt-8 max-w-md mx-auto">
            <EmailOptInForm source="resources_hero" compact />
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-24">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROMPT_CATEGORIES.map((cat) => (
            <motion.div key={cat.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5 }} className="border border-white/10 rounded-sm p-6 bg-surface/40">
              <cat.icon className="w-6 h-6 text-primary mb-4" aria-hidden="true" />
              <h3 className="font-display font-bold text-white text-lg">{cat.label}</h3>
              <ul className="mt-4 space-y-2.5">
                {cat.prompts.map((p) => {
                  idx += 1;
                  return (
                    <li key={p} data-testid={RESOURCES_PAGE.promptItem(idx)} className="flex gap-2 text-sm text-zinc-400 leading-relaxed">
                      <span className="text-gold shrink-0">{String(idx).padStart(2, "0")}</span>{p}
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-24 border-t border-white/10 pt-16">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono-tag text-xs uppercase tracking-[0.2em] text-gold mb-6">Templates</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {TEMPLATE_GUIDES.map((g) => (
              <Link key={g.slug} to={`/guides/${g.slug}`} className="group border border-white/10 rounded-sm overflow-hidden hover:border-primary/50 transition-colors">
                <img src={g.heroImage} alt={g.title} className="w-full h-32 object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                <div className="p-4">
                  <p className="text-white text-sm font-medium leading-snug line-clamp-2">{g.title}</p>
                  <p className="text-primary text-xs font-medium mt-2 inline-flex items-center gap-1">Open template <ArrowRight className="w-3 h-3" aria-hidden="true" /></p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-24 border-t border-white/10 pt-16">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono-tag text-xs uppercase tracking-[0.2em] text-gold mb-6">Case Studies</p>
          <div className="grid sm:grid-cols-2 gap-6">
            {CASE_STUDIES.map((g) => (
              <Link key={g.slug} to={`/guides/${g.slug}`} className="group flex gap-4 border border-white/10 rounded-sm p-5 hover:border-primary/50 transition-colors bg-surface/40">
                <img src={g.heroImage} alt={g.title} className="w-24 h-24 object-cover rounded-sm grayscale group-hover:grayscale-0 transition-all duration-500 shrink-0" />
                <div>
                  <p className="text-white font-display font-bold text-sm leading-snug">{g.title}</p>
                  <p className="text-primary text-xs font-medium mt-2 inline-flex items-center gap-1">Read case study <ArrowRight className="w-3 h-3" aria-hidden="true" /></p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
